import { Table, Tag } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import ReplaceStrings from '../../../../config/replaceStrings';
import Routes from '../../../../config/routes';
import { translations } from '../../../../config/translations';
import { formatDate } from '../../../../helpers/DateHelper';
import { getTableLocale } from '../../../../helpers/TableHelper';
import { ToolTransferVm, ToolTransfersClient } from '../../../../utils/api';
import TransferStatusTag from '../../../transfers/transfer-status-tag/TransferStatusTag';

interface Props {
    toolId: number;
}

interface State {
    transfers: ToolTransferVm[];
    loading: boolean;
}

/**
 * Read-only transfer history for one tool (spec section 10). Actions stay on the Transferi page.
 * A site manager only gets transfers touching his own warehouses - the endpoint filters that.
 */
class ToolTransfers extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            transfers: [],
            loading: false,
        };
    }

    public componentDidMount = () => {
        void this.getTransfers();
    };

    private getTransfers = async () => {
        const { toolId } = this.props;

        this.setState({ loading: true });

        const transfers = await new ToolTransfersClient().getByTool(toolId);

        this.setState({ transfers, loading: false });
    };

    /** Whether this particular tool arrived on that transfer, not the transfer as a whole. */
    private renderToolOutcome = (transfer: ToolTransferVm): React.ReactElement => {
        const { toolId } = this.props;
        const item = (transfer.items || []).find((i) => i.toolId === toolId);

        if (!item || item.received == null) {
            return <Tag>{translations.transfers.inTransfer}</Tag>;
        }

        return item.received ? (
            <Tag color="green">{translations.transfers.arrived}</Tag>
        ) : (
            <Tag color="red">{translations.transfers.notArrived}</Tag>
        );
    };

    public render(): React.ReactElement {
        const { toolId } = this.props;
        const { transfers, loading } = this.state;

        const columns = [
            {
                title: translations.transfers.fromWarehouse,
                dataIndex: 'fromName',
                render: (value: string, record: ToolTransferVm): React.ReactElement => (
                    <Link
                        to={Routes.ROUTE_TRANSFERS_READ.replace(
                            ReplaceStrings.ENTITY_ID,
                            record.id.toString()
                        )}
                    >
                        {value}
                    </Link>
                ),
            },
            {
                title: translations.transfers.toWarehouse,
                dataIndex: 'toName',
            },
            {
                title: translations.transfers.createdBy,
                dataIndex: 'createdByName',
            },
            {
                title: translations.transfers.createdDate,
                dataIndex: 'createdDate',
                render: (value: Date): string => formatDate(value),
            },
            {
                title: translations.transfers.status,
                key: 'status',
                render: (_: unknown, record: ToolTransferVm): React.ReactElement => (
                    <TransferStatusTag status={record.status} />
                ),
            },
            {
                title: translations.transfers.shippedBy,
                key: 'shippedBy',
                render: (_: unknown, record: ToolTransferVm): string =>
                    record.shippedByName
                        ? record.shippedByName + ' (' + formatDate(record.shippedDate) + ')'
                        : '',
            },
            {
                title: translations.transfers.receivedBy,
                key: 'receivedBy',
                render: (_: unknown, record: ToolTransferVm): string =>
                    record.receivedByName
                        ? record.receivedByName + ' (' + formatDate(record.receivedDate) + ')'
                        : '',
            },
            {
                title: translations.transfers.arrived,
                key: 'toolOutcome',
                render: (_: unknown, record: ToolTransferVm): React.ReactElement =>
                    this.renderToolOutcome(record),
            },
            {
                title: translations.transfers.lostReason,
                key: 'lostReason',
                render: (_: unknown, record: ToolTransferVm): string =>
                    (record.items || []).find((i) => i.toolId === toolId)?.lostReason || '',
            },
        ];

        return (
            <Table
                locale={getTableLocale()}
                columns={columns}
                dataSource={transfers}
                loading={loading}
                rowKey={(record: ToolTransferVm): string => record.id.toString()}
                pagination={false}
                bordered
                size="small"
            />
        );
    }
}

export default ToolTransfers;
